import m from "mithril";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
// const DAY_MS = 24*HOUR_MS;

// How long to wait if we are asked to re-load "eventually"
const EVENTUALLY_MS = HOUR_MS;

// Poll this often on average whether we need to re-load
const POLL_MS = 30000;

// Apply this much fuzz to POLL_MS
const POLL_FUZZ_MS = 10000;

/**
 * DataFetcher factory. It encapsulates the state of a polling re-fetch timeout, which is configured and started and then
 * re-configured by calling the returned fetcher function.
 *
 * @param {Map} opts Configuration object with a `req` function and `success` and `error` handlers. It can have a `label` to use for logging.
 * @returns {fetcher}
 */
export default function DataFetcher(opts) {

    if (!opts.label) opts.label = 'fetcher';

    /** The last arguments passed to `fetcher(...)`. They will be re-used for subsequent re-loads. */
    let old_args;

    /** The timeout for calling `trigger()` to check if we need to re-load data. */
    let timeout;

    /** The timestamp when we next need to fetch data. */
    let fetchAtMs;

    /** Cancel this fetcher. */
    function cancel() {
        timeout = clearTimeout(timeout);
        fetchAtMs = undefined;
    }

    /**
     * If the fetchAtMs time has arrived, re-fetch data.
     * Otherwise, if there is a fetchAtMs, re-schedule another trigger.
     * Otherwise, do nothing.
     * Calling trigger when no fetcher are needed is harmless.
     */
    function trigger() {
        if (fetchAtMs === undefined) {
            //console.log(opts.label + '.trigger with no fetchAtMs');
            return;
        }
        const now = new Date().getTime();
        //console.log(opts.label + '.trigger', Math.round((fetchAtMs - now) / 1000, 1));
        if (now >= fetchAtMs) {
            fetcher();
        } else {
            schedule();
        }
    }

    /**
     * Schedule a trigger to run in a randomised time
     * @param fetchAt if given, set the fetchAtMs time. A true means to re-fetch "in a good while", Date, Number or
     *                String are taken as the actual time to fetch at.
     */
    function schedule(fetchAt) {
        if (fetchAt !== undefined) {
            //console.log(opts.label + '.schedule', fetchAt);
            fetchAtMs =
                fetchAt === true ? new Date().getTime() + EVENTUALLY_MS :
                fetchAt instanceof Number ? fetchAt :
                fetchAt instanceof Date ? fetchAt.getTime() :
                new Date(fetchAt).getTime();
            //console.log(opts.label + '.reschedule:', new Date(fetchAtMs));
        }
        const waitMs = POLL_MS + (Math.random() - 0.5) * 2 * POLL_FUZZ_MS;
        //console.log(opts.label + '.setTimeout', Math.round(waitMs / 1000, 1));
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(trigger, waitMs);
    }

    /**
     * DataFetcher. Calling it causes an immediate request to be made and a timeout is set which periodically checks
     * whether it is time to re-fetch.
     *
     * @param {map} args Arguments passed to opts.req specified when constructing this DataFetcher
     */
    function fetcher(args) {
        //console.log(opts.label + '.fetcher:', args);
        args = old_args = (args === undefined) ? old_args : args;
        cancel();

        const req = Object.assign({
            method: 'GET',
            background: true,
        }, opts.req(args));

        //console.log(opts.label + '.request:', req.url);
        m.request(req)
        //.then(data => console.log(opts.label, 'returned', data) || data)
            .then(data => opts.success(data, args) || {})
            .then(res => {
                //console.log(opts.label + '.then', res);
                if (res.expires) {
                    //console.log(opts.label + '.reschedule:', res.expires);
                    schedule(res.expires);
                } else {
                    console.log(opts.label + ' not re-scheduling');
                }
                if (res.redraw !== false) {
                    m.redraw();
                }
            })
            .catch(errorResult => {
                    console.error(opts.label + '.catch', errorResult);
                if (opts.failure) {
                    opts.failure(errorResult, args);
                }
                m.redraw();
                schedule(0);
            });
    }

    fetcher.cancel = cancel;
    fetcher.trigger = trigger;

    /** In case we missed a deadline while the network was down */
    window.addEventListener('online', e => {
        clearTimeout(timeout);
        //console.log(opts.label + '.online-event');
        timeout = setTimeout(trigger, 2000 + Math.random() * 1000);
    });

    return fetcher;
}
